import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { usePermissions } from '../../hooks/usePermissions';
import { ForbiddenState } from '../common/ForbiddenState';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** @deprecated Use requiredPermission instead. Kept for backward compatibility. */
  allowedRoles?: string[];
  /** Permission-based guard: { obj, act } checked via Casbin policy. */
  requiredPermission?: { obj: string; act: string };
  /**
   * OR-of-permissions guard: route is allowed when ANY listed permission is
   * held. Used for pages whose content varies by sub-permission (e.g. the
   * Work Orders page shows Roasting + Packing tabs and any holder of either
   * `work_orders.roasting:read` or `work_orders.packing:read` may enter).
   * Evaluated after `requiredPermission` if both are provided.
   */
  requiredAnyOf?: Array<{ obj: string; act: string }>;
  /**
   * Legacy redirect target for denied access. When omitted (preferred), the
   * ForbiddenState page is rendered in-place so the user understands *why*
   * they landed here instead of being silently bounced elsewhere.
   */
  redirectTo?: string;
}

const normalizeRole = (role: string | null | undefined): string => (role ?? '').trim().toLowerCase();

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  allowedRoles,
  requiredPermission,
  requiredAnyOf,
  redirectTo,
}) => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const userRole = useAuthStore((state) => state.user?.role);
  const location = useLocation();
  const { can } = usePermissions();

  if (!isAuthenticated) {
    const redirectTarget = `${location.pathname}${location.search}${location.hash}`;
    const loginParams = new URLSearchParams({ redirect: redirectTarget });
    return <Navigate to={`/login?${loginParams.toString()}`} state={{ from: location }} replace />;
  }

  const denyResponse = () =>
    redirectTo ? <Navigate to={redirectTo} replace /> : <ForbiddenState variant="page" />;

  // Permission-based guard (preferred)
  if (requiredPermission && !can(requiredPermission.obj, requiredPermission.act)) {
    return denyResponse();
  }

  if (requiredAnyOf && requiredAnyOf.length > 0) {
    const anyHeld = requiredAnyOf.some((p) => can(p.obj, p.act));
    if (!anyHeld) {
      return denyResponse();
    }
  }

  // Legacy role-based guard (backward compatibility)
  if (allowedRoles && allowedRoles.length > 0) {
    const normalizedUserRole = normalizeRole(userRole);
    const isAllowed = allowedRoles.some((role) => normalizeRole(role) === normalizedUserRole);

    if (!isAllowed) {
      return denyResponse();
    }
  }

  return <>{children}</>;
};
